
import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useCrypto } from '../../hooks/useCrypto';
import Header from '../../components/feature/Header';
import Card from '../../components/base/Card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const { user } = useAuth();
  const { cryptoData, portfolio, transactions, loading } = useCrypto();
  const navigate = useNavigate();

  // Calculate portfolio metrics
  const totalPortfolioValue = portfolio.reduce((sum, asset) => sum + (asset.totalValue || 0), 0);
  const totalProfit = portfolio.reduce((sum, asset) => sum + (asset.profit || 0), 0);
  
  // Add error handling for division by zero
  const totalProfitPercent = totalPortfolioValue > 0 && (totalPortfolioValue - totalProfit) > 0 
    ? (totalProfit / (totalPortfolioValue - totalProfit)) * 100 
    : 0;

  // Mock chart data - in a real app this would come from actual portfolio history
  const chartData = [
    { name: 'Jan', value: 45000 },
    { name: 'Feb', value: 52000 },
    { name: 'Mar', value: 48000 },
    { name: 'Apr', value: 61000 },
    { name: 'May', value: 55000 },
    { name: 'Jun', value: 67000 },
    { name: 'Jul', value: totalPortfolioValue },
  ];

  // Pie chart data for asset allocation
  const pieData = portfolio.map((asset, index) => ({
    name: asset.symbol,
    value: asset.totalValue || 0,
    color: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'][index % 5]
  }));

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  // Add error handling for empty portfolio when determining best performer
  const bestPerformingAsset = portfolio.length > 0 
    ? portfolio.reduce((best, current) => 
        (current.profitPercent || 0) > (best.profitPercent || 0) ? current : best
      )
    : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome back, {user?.name || 'User'}!
          </h1>
          <p className="text-gray-600">Here's your crypto portfolio overview</p>
        </div>

        {/* Portfolio Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-600">Total Portfolio</h3>
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-wallet-3-line text-xl text-blue-500"></i>
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-2xl font-bold text-gray-900">
                ${totalPortfolioValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className={`text-sm flex items-center ${
                totalProfitPercent >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                <i className={`ri-arrow-${totalProfitPercent >= 0 ? 'up' : 'down'}-line mr-1`}></i>
                {totalProfitPercent >= 0 ? '+' : ''}{totalProfit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({totalProfitPercent.toFixed(2)}%)
              </p>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-600">Active Assets</h3>
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-coins-line text-xl text-green-500"></i>
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-2xl font-bold text-gray-900">{portfolio.length}</p>
              <p className="text-sm text-gray-600">Different cryptocurrencies</p>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-600">Best Performer</h3>
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-trophy-line text-xl text-yellow-500"></i>
              </div>
            </div>
            <div className="space-y-1">
              {bestPerformingAsset ? (
                <>
                  <p className="text-2xl font-bold text-gray-900">
                    {bestPerformingAsset.symbol}
                  </p>
                  <p className="text-sm text-green-600">
                    +{bestPerformingAsset.profitPercent?.toFixed(2) || '0.00'}%
                  </p>
                </>
              ) : (
                <>
                  <p className="text-2xl font-bold text-gray-900">--</p>
                  <p className="text-sm text-gray-600">No assets yet</p>
                </>
              )}
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-600">Total Transactions</h3>
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-exchange-line text-xl text-purple-500"></i>
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-2xl font-bold text-gray-900">{transactions?.length || 0}</p>
              <p className="text-sm text-gray-600">All time activity</p>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Portfolio Chart */}
          <Card className="lg:col-span-2 p-6">
            <h3 className="text-lg font-semibold mb-6">Portfolio Performance</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value) => [`$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 'Portfolio Value']} />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#3B82F6" 
                    strokeWidth={3}
                    dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Asset Allocation */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-6">Asset Allocation</h3>
            {portfolio.length > 0 ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 'Value']} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-4 space-y-2">
                  {pieData.map((entry, index) => (
                    <div key={entry.name} className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        ></div>
                        <span className="text-sm font-medium">{entry.name}</span>
                      </div>
                      <span className="text-sm text-gray-600">
                        {totalPortfolioValue > 0 ? ((entry.value / totalPortfolioValue) * 100).toFixed(1) : '0.0'}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-80 flex items-center justify-center">
                <div className="text-center">
                  <i className="ri-pie-chart-line text-4xl text-gray-400 mb-4"></i>
                  <p className="text-gray-600">No assets to display</p>
                  <p className="text-sm text-gray-500">Start trading to see your allocation</p>
                </div>
              </div>
            )}
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Holdings */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-6">Your Holdings</h3>
            {portfolio.length > 0 ? (
              <div className="space-y-4">
                {portfolio.map((asset) => {
                  const coinData = cryptoData?.find(coin => coin.symbol?.toLowerCase() === asset.symbol?.toLowerCase());
                  return (
                    <div key={asset.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        {coinData ? (
                          <img src={coinData.image} alt={asset.name} className="w-10 h-10" />
                        ) : (
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="font-bold text-blue-600">{asset.symbol?.substring(0, 3)}</span>
                          </div>
                        )}
                        <div>
                          <p className="font-semibold">{asset.symbol}</p>
                          <p className="text-sm text-gray-600">{(asset.amount || 0).toFixed(6)} {asset.symbol}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">${(asset.totalValue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                        <p className={`text-sm ${
                          (asset.profitPercent || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {(asset.profitPercent || 0) >= 0 ? '+' : ''}{(asset.profitPercent || 0).toFixed(2)}%
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <i className="ri-coins-line text-4xl text-gray-400 mb-4"></i>
                <p className="text-gray-600">No holdings yet</p>
                <p className="text-sm text-gray-500">Start trading to build your portfolio</p>
              </div>
            )}
          </Card>

          {/* Recent Activity */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-6">Recent Activity</h3>
            {transactions && transactions.length > 0 ? (
              <div className="space-y-4">
                {transactions.slice(0, 5).map((tx) => (
                  <div key={tx.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 flex items-center justify-center rounded-full ${
                        tx.type === 'buy' || tx.type === 'deposit' ? 'bg-green-100' :
                        tx.type === 'sell' || tx.type === 'withdrawal' ? 'bg-red-100' : 'bg-blue-100'
                      }`}>
                        <i className={`ri-${
                          tx.type === 'buy' ? 'add' :
                          tx.type === 'sell' ? 'subtract' :
                          tx.type === 'deposit' ? 'arrow-down' :
                          tx.type === 'withdrawal' ? 'arrow-up' :
                          tx.type === 'convert' ? 'exchange' : 'send'
                        }-line text-sm ${
                          tx.type === 'buy' || tx.type === 'deposit' ? 'text-green-600' :
                          tx.type === 'sell' || tx.type === 'withdrawal' ? 'text-red-600' : 'text-blue-600'
                        }`}></i>
                      </div>
                      <div>
                        <p className="font-medium capitalize">{tx.type}</p>
                        <p className="text-sm text-gray-600">
                          {tx.type === 'convert' ? 
                            `${tx.fromAsset} → ${tx.toAsset}` : 
                            tx.asset
                          }
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">
                        {tx.type === 'convert' ? 
                          `${tx.amount} → ${tx.toAmount?.toFixed(4)}` : 
                          tx.amount?.toLocaleString()
                        }
                      </p>
                      <p className="text-sm text-gray-600">
                        {new Date(tx.date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <i className="ri-history-line text-4xl text-gray-400 mb-4"></i>
                <p className="text-gray-600">No recent activity</p>
                <p className="text-sm text-gray-500">Your transactions will appear here</p>
              </div>
            )}
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="p-6 mt-6">
          <h3 className="text-lg font-semibold mb-6">Quick Actions</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button
              onClick={() => navigate('/trading')}
              className="flex flex-col items-center justify-center p-6 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-8 h-8 flex items-center justify-center mb-2">
                <i className="ri-arrow-up-line text-2xl"></i>
              </div>
              <span className="font-medium">Buy Crypto</span>
            </button>

            <button
              onClick={() => navigate('/trading')}
              className="flex flex-col items-center justify-center p-6 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-8 h-8 flex items-center justify-center mb-2">
                <i className="ri-arrow-down-line text-2xl"></i>
              </div>
              <span className="font-medium">Sell Crypto</span>
            </button>

            <button
              onClick={() => navigate('/convert')}
              className="flex flex-col items-center justify-center p-6 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-8 h-8 flex items-center justify-center mb-2">
                <i className="ri-exchange-line text-2xl"></i>
              </div>
              <span className="font-medium">Convert</span>
            </button>

            <button
              onClick={() => navigate('/wallet')}
              className="flex flex-col items-center justify-center p-6 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-8 h-8 flex items-center justify-center mb-2">
                <i className="ri-send-plane-line text-2xl"></i>
              </div>
              <span className="font-medium">Transfer</span>
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
